import { useState } from "react";
import { Swords, ChevronRight, X, Plus, ScrollText, Trash2, Heart, Skull, Sparkles, Tag } from "lucide-react";
import { toast } from "sonner";

// Floating combat / initiative tracker popup — presentation only.
export const VTTCombatTracker = ({
  show,
  initiativeOrder,
  activeSession,
  combatActive,
  selectedToken,
  setInitiativeOrder,
  setActiveInitiative,
  setSelectedToken,
  addToInitiative,
  removeFromInitiative,
  onClose,
  onEndCombat,
  combatLog = [],
  pushCombatLog,
  clearCombatLog,
}) => {
  const [tab, setTab] = useState("init"); // "init" | "log"

  if (!show || !activeSession || initiativeOrder.length === 0) return null;

  const activeIdx = initiativeOrder.findIndex((i) => i.isActive);
  const roundLabel =
    Math.floor(initiativeOrder.filter((i) => i.isActive).length > 0 ? 1 : 0) +
    Math.floor(activeIdx >= 0 ? 1 : 0);

  const handleNext = () => {
    const nextIdx = activeIdx < initiativeOrder.length - 1 ? activeIdx + 1 : 0;
    setActiveInitiative(nextIdx);
    const entry = initiativeOrder[nextIdx];
    const tok = activeSession?.tokens?.find((t) => t.id === entry?.id);
    if (tok) setSelectedToken(tok);
    if (pushCombatLog && entry) {
      const wraps = nextIdx === 0 && activeIdx >= 0;
      pushCombatLog("turn", `${wraps ? "New round — " : ""}${entry.name}'s turn`);
    }
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
    catch { return ""; }
  };
  const KIND_ICON = {
    damage: <span className="text-red-400">−HP</span>,
    heal: <Heart className="w-2.5 h-2.5 text-green-400" />,
    death: <Skull className="w-2.5 h-2.5 text-red-500" />,
    condition: <Tag className="w-2.5 h-2.5 text-amber-400" />,
    init: <Plus className="w-2.5 h-2.5 text-primary" />,
    turn: <ChevronRight className="w-2.5 h-2.5 text-primary" />,
    spell: <Sparkles className="w-2.5 h-2.5 text-purple-400" />,
  };

  return (
    <div
      className="absolute top-12 right-12 z-30 bg-[#1a1a2e]/95 backdrop-blur-md border border-[#444] rounded-lg shadow-2xl w-72"
      data-vtt-sidebar="combat"
      data-testid="initiative-tracker"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#444] bg-red-900/20 rounded-t-lg">
        <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
          <Swords className="w-3.5 h-3.5 text-red-400" />
          {combatActive ? "Combat Tracker" : "Initiative Order"}
        </h4>
        <div className="flex gap-1">
          <button
            className="text-[#888] hover:text-green-400 p-0.5 transition-colors"
            onClick={handleNext}
            title="Next Turn"
            data-testid="next-initiative"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button className="text-[#888] hover:text-white p-0.5" onClick={onClose}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#333] bg-[#111]">
        <button
          onClick={() => setTab("init")}
          className={`flex-1 text-[10px] py-1.5 uppercase tracking-wider flex items-center justify-center gap-1 transition-colors ${tab === "init" ? "bg-red-900/30 text-white" : "text-[#888] hover:text-white"}`}
          data-testid="tab-initiative"
        >
          <Swords className="w-3 h-3" /> Initiative
        </button>
        <button
          onClick={() => setTab("log")}
          className={`flex-1 text-[10px] py-1.5 uppercase tracking-wider flex items-center justify-center gap-1 transition-colors ${tab === "log" ? "bg-red-900/30 text-white" : "text-[#888] hover:text-white"}`}
          data-testid="tab-combat-log"
        >
          <ScrollText className="w-3 h-3" /> Log <span className="text-[#666]">({combatLog.length})</span>
        </button>
      </div>

      {tab === "init" ? (
        <>
          <div className="px-3 py-1 border-b border-[#333] flex items-center justify-between">
            <span className="text-[9px] text-[#888] uppercase">Round {roundLabel}</span>
            <span className="text-[9px] text-[#666]">{initiativeOrder.length} combatants</span>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {initiativeOrder.map((entry, idx) => {
              const token = activeSession?.tokens?.find((t) => t.id === entry.id);
              const isDead = token?.is_dead;
              return (
                <div
                  key={entry.id}
                  className={`flex items-center gap-2 px-3 py-1.5 border-b border-[#333]/50 last:border-0 cursor-pointer transition-colors ${entry.isActive ? "bg-red-500/15 border-l-2 border-l-red-500" : "hover:bg-[#333]/30"} ${isDead ? "opacity-40" : ""}`}
                  onClick={() => { setActiveInitiative(idx); if (token) setSelectedToken(token); }}
                  data-testid={`init-entry-${entry.id}`}
                >
                  <span className={`text-xs font-bold w-6 text-center rounded-sm py-0.5 ${entry.isActive ? "bg-red-500/30 text-red-300" : "text-primary"}`}>
                    {entry.initiative}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs truncate block ${entry.isActive ? "text-white font-bold" : "text-[#ccc]"}`}>
                      {entry.isActive ? "\u25B6 " : ""}{entry.name}{isDead ? " \u2620" : ""}
                    </span>
                    {token && <span className="text-[8px] text-[#666]">{token.hp}/{token.max_hp} HP | AC {token.ac}</span>}
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    <input
                      className="w-8 h-5 text-[9px] text-center bg-[#333] border border-[#555] rounded-sm text-white"
                      value={entry.initiative}
                      onChange={(e) => {
                        const v = parseInt(e.target.value);
                        if (!isNaN(v)) setInitiativeOrder((p) => p.map((i) => i.id === entry.id ? { ...i, initiative: v } : i).sort((a, b) => b.initiative - a.initiative));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`init-value-${entry.id}`}
                    />
                    <button
                      className="text-[#666] hover:text-red-400 p-0.5"
                      onClick={(e) => { e.stopPropagation(); removeFromInitiative(entry.id); }}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-3 py-1.5 border-t border-[#444] space-y-1">
            <button
              className="w-full text-[10px] text-primary hover:text-white py-0.5 transition-colors flex items-center justify-center gap-1"
              onClick={() => {
                if (selectedToken && !initiativeOrder.find((i) => i.id === selectedToken.id)) {
                  const init = Math.floor(Math.random() * 20) + 1;
                  addToInitiative(selectedToken, init);
                  toast.success(`Added ${selectedToken.name} (init: ${init})`);
                } else {
                  toast.info("Select a token on the map to add it");
                }
              }}
              data-testid="add-to-combat"
            >
              <Plus className="w-3 h-3" /> Add Selected Token
            </button>
            {combatActive && (
              <button
                className="w-full text-[10px] text-amber-400 hover:text-amber-300 py-0.5 transition-colors"
                onClick={onEndCombat}
                data-testid="end-combat"
              >
                End Combat
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="px-3 py-1 border-b border-[#333] flex items-center justify-between">
            <span className="text-[9px] text-[#888] uppercase">Combat Log</span>
            <button
              onClick={clearCombatLog}
              disabled={combatLog.length === 0}
              className="text-[9px] text-[#888] hover:text-red-400 flex items-center gap-1 disabled:opacity-30"
              data-testid="clear-combat-log"
            >
              <Trash2 className="w-2.5 h-2.5" /> Clear
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto py-1" data-testid="combat-log-list">
            {combatLog.length === 0 ? (
              <p className="text-[10px] text-[#666] italic px-3 py-3 text-center">
                Damage, heals, conditions and turns will be logged here as combat plays out.
              </p>
            ) : (
              combatLog.slice().reverse().map((entry) => (
                <div key={entry.id} className="flex items-start gap-1.5 px-3 py-1 text-[11px] border-b border-[#333]/30 last:border-0" data-testid={`combat-log-${entry.id}`}>
                  <span className="flex-shrink-0 mt-0.5">{KIND_ICON[entry.kind] || <ScrollText className="w-2.5 h-2.5 text-[#666]" />}</span>
                  <span className="flex-1 text-[#ddd] leading-snug">{entry.text}</span>
                  <span className="text-[9px] text-[#555] flex-shrink-0">{fmtTime(entry.ts)}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
